import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Receipt, Loader2, AlertTriangle, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Génération d'un avoir FNE depuis la fiche opération (sans passer par l'espace FNE).
 * Annulation totale par défaut ; quantités partielles possibles ligne par ligne.
 */
export function FneCreditNoteDialog({
  open,
  onOpenChange,
  operationId,
  invoiceLabel,
  certification,
  onSuccess,
}) {
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState('total');
  const [quantities, setQuantities] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const itemsMap = certification?.itemsMap || [];
  const alreadyRefunded = certification?.refundedQuantities || {};

  useEffect(() => {
    if (!open) return;
    setReason('');
    setMode('total');
    setQuantities(Object.fromEntries(itemsMap.map((line) => {
      const id = String(line.fneItemId);
      const remaining = Number(line.quantity || 0) - Number(alreadyRefunded[id] || 0);
      return [id, remaining > 0 ? remaining : 0];
    })));
    axios.get(`${API}/fne/credit-note-reasons`)
      .then((res) => setSuggestions(res.data?.reasons || []))
      .catch(() => setSuggestions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const buildItems = () => Object.entries(quantities)
    .filter(([, qty]) => Number(qty) > 0)
    .map(([id, qty]) => ({ id, quantity: Number(qty) }));

  const openPreview = async () => {
    const payload = { targetType: 'spot', reason: reason.trim() };
    if (mode === 'partial') {
      const items = buildItems();
      if (!items.length) {
        toast.error('Indiquez au moins une quantité à créditer');
        return;
      }
      payload.items = items;
    }
    setPreviewing(true);
    const win = window.open('', '_blank');
    try {
      const res = await axios.post(`${API}/fne/credit-note-preview/${operationId}`, payload, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (win) {
        win.location.href = url;
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = 'apercu-avoir.pdf';
        a.click();
        toast.info("Aperçu téléchargé (fenêtres pop-up bloquées par le navigateur)");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (win) win.close();
      toast.error("Aperçu indisponible — vérifiez les quantités saisies");
    } finally {
      setPreviewing(false);
    }
  };

  const submit = async () => {
    if (!reason.trim() || reason.trim().length < 5) {
      toast.error("Motif obligatoire (5 caractères minimum) — il figure sur l'avoir");
      return;
    }
    const payload = { targetType: 'spot', reason: reason.trim() };
    if (mode === 'partial') {
      const items = buildItems();
      if (!items.length) {
        toast.error('Indiquez au moins une quantité à créditer');
        return;
      }
      payload.items = items;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/fne/refund/${operationId}`, payload);
      if (res.data?.success) {
        toast.success(
          res.data.creditNoteNumber
            ? `Avoir ${res.data.creditNoteNumber} généré et certifié`
            : 'Avoir certifié par la DGI',
        );
        onOpenChange(false);
        onSuccess?.();
      } else {
        toast.error(res.data?.errorMessage || "La DGI a refusé l'avoir");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || "Génération de l'avoir impossible");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-[calc(100%-1.5rem)] max-h-[90vh] overflow-y-auto rounded-lg" data-testid="fne-credit-note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <Receipt className="w-4 h-4" /> Générer un avoir
          </DialogTitle>
          <DialogDescription>
            Annule tout ou partie de la facture <strong>{invoiceLabel}</strong> et transmet
            l&apos;avoir à la DGI. L&apos;avoir certifié est envoyé au client par e-mail.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex gap-2">
            <Button
              type="button"
              variant={mode === 'total' ? 'default' : 'outline'}
              size="sm"
              className={mode === 'total' ? 'flex-1 bg-rose-600 hover:bg-rose-700' : 'flex-1'}
              onClick={() => setMode('total')}
              data-testid="credit-note-mode-total"
            >
              Annulation totale
            </Button>
            <Button
              type="button"
              variant={mode === 'partial' ? 'default' : 'outline'}
              size="sm"
              className={mode === 'partial' ? 'flex-1 bg-rose-600 hover:bg-rose-700' : 'flex-1'}
              onClick={() => setMode('partial')}
              disabled={!itemsMap.length}
              data-testid="credit-note-mode-partial"
            >
              Avoir partiel
            </Button>
          </div>

          {mode === 'partial' && (
            <div className="space-y-2 max-h-56 overflow-y-auto border rounded-md p-2 bg-slate-50">
              {itemsMap.map((line) => {
                const id = String(line.fneItemId);
                const remaining = Number(line.quantity || 0) - Number(alreadyRefunded[id] || 0);
                const unitPrice = Number(line.amount || 0) / (Number(line.quantity) || 1);
                const credited = Number(quantities[id] || 0) * unitPrice;
                return (
                  <div key={id} className="bg-white border rounded-md px-2.5 py-2">
                    <p className="text-xs text-slate-700 leading-snug line-clamp-2" title={line.description}>
                      {line.description || id}
                    </p>
                    <div className="flex items-center justify-between gap-2 mt-1.5">
                      <span className="text-[11px] text-slate-500 shrink-0">
                        Reste <strong className="text-slate-700">{remaining}</strong>
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-slate-500">Qté à créditer</span>
                        <Input
                          type="number"
                          min="0"
                          max={remaining}
                          step="0.01"
                          value={quantities[id] ?? 0}
                          onChange={(e) => {
                            const raw = Number(e.target.value);
                            const clamped = Number.isNaN(raw) ? 0 : Math.min(Math.max(raw, 0), remaining);
                            setQuantities((q) => ({ ...q, [id]: clamped }));
                          }}
                          className="w-16 h-7 text-xs text-center px-1"
                          data-testid={`credit-note-qty-${id}`}
                        />
                        <span className="text-[11px] font-semibold text-rose-700 w-24 text-right tabular-nums">
                          {credited.toLocaleString('fr-FR')} F
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {mode === 'partial' && (
            <div className="flex items-center justify-between text-xs bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
              <span className="text-rose-800">Total à créditer</span>
              <span className="font-bold text-rose-800 tabular-nums" data-testid="credit-note-total">
                {itemsMap.reduce((sum, line) => {
                  const id = String(line.fneItemId);
                  const unitPrice = Number(line.amount || 0) / (Number(line.quantity) || 1);
                  return sum + Number(quantities[id] || 0) * unitPrice;
                }, 0).toLocaleString('fr-FR')} F CFA
              </span>
            </div>
          )}

          <div>
            <Label htmlFor="credit-note-reason" className="text-xs">Motif de l&apos;avoir</Label>
            {suggestions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="credit-note-reason-suggestions">
                {suggestions.map((s) => (
                  <button
                    key={s.reason}
                    type="button"
                    aria-pressed={reason === s.reason}
                    onClick={() => setReason(s.reason)}
                    className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                      reason === s.reason
                        ? 'bg-rose-600 border-rose-600 text-white'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-rose-300 hover:text-rose-700'
                    }`}
                    data-testid={`credit-note-reason-chip-${s.reason.slice(0, 18).replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {s.reason}
                    {s.count > 0 && <span className="ml-1 opacity-60">×{s.count}</span>}
                  </button>
                ))}
              </div>
            )}
            <Textarea
              id="credit-note-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex. : rotation annulée par le client, erreur de facturation…"
              rows={2}
              className="mt-1 text-sm"
              data-testid="credit-note-reason-input"
            />
          </div>

          <div className="flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Opération irréversible : l&apos;avoir est certifié par la DGI et consomme un sticker.
              La facture d&apos;origine sera marquée « annulée par avoir ».
            </span>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="sm:order-1"
          >
            Annuler
          </Button>
          <Button
            className="bg-rose-600 hover:bg-rose-700 text-white sm:order-3"
            onClick={submit}
            disabled={saving}
            data-testid="credit-note-submit-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Transmission DGI…</>
              : <><Receipt className="w-4 h-4 mr-1" /> Générer l&apos;avoir</>}
          </Button>
          <Button
            variant="outline"
            className="border-slate-300 text-slate-700 sm:order-2"
            onClick={openPreview}
            disabled={saving || previewing}
            data-testid="credit-note-preview-btn"
          >
            {previewing ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Aperçu…</>
              : <><Eye className="w-4 h-4 mr-1" /> Aperçu PDF</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default FneCreditNoteDialog;
